// src/components/practitioners/PractitionerStammdatenForm_useState.tsx
"use client";

import { isEqual } from 'lodash';
import { ImagePlus, Loader2 } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { useShallow } from "zustand/react/shallow";
import { trpc } from "~/api/trpc";
import { PractitionerLeistungenSelector } from "~/components/practitioners/new-form/PractitionerLeistungenSelector";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { MultiSelect, MultiSelectOption } from "~/components/ui/multi-select";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import { useUserStore } from "~/stores/user-store";
import { UserRole } from "~/types/server-types/UserRole";
// Import the new component

// PractitionerType enum values - must match the Prisma enum
const PractitionerType = {
    DOCTOR: 'DOCTOR',
    COSMETICIAN: 'COSMETICIAN',
    MFA: 'MFA'
} as const;

// Define the Zod schema for validation (add performableTreatmentVariantIds)
const formSchema = z.object({
    title: z.string().max(255, "Titel darf maximal 255 Zeichen lang sein.").nullable().default(null),
    firstName: z.string().min(1, "Vorname ist erforderlich.").max(255),
    lastName: z.string().min(1, "Nachname ist erforderlich.").max(255),
    categoryId: z.number().nullable(),
    bookingPriority: z.number(),
    type: z.enum([PractitionerType.DOCTOR, PractitionerType.COSMETICIAN, PractitionerType.MFA]),
    storeIds: z.array(z.number()).min(1, "Mindestens eine Filiale ist erforderlich."),
    performableTreatmentVariantIds: z.array(z.string()), // Added: No min requirement for now
});

type PractitionerFormData = z.infer<typeof formSchema>;

interface PractitionerStammdatenFormProps {
    practitionerId: number;
}

const NULL_CATEGORY_VALUE = "__NONE__";

// Initial empty state matching the schema structure and defaults
const initialFormData: PractitionerFormData = {
    title: null, // Use null consistently
    firstName: "",
    lastName: "",
    categoryId: null,
    bookingPriority: 100,
    type: PractitionerType.DOCTOR,
    storeIds: [],
    performableTreatmentVariantIds: [], // Added
};

export function PractitionerStammdatenForm({ practitionerId }: PractitionerStammdatenFormProps) {
    const { userRole } = useUserStore(
        useShallow((state) => ({
            userRole: state.user?.role
        }))
    );
    const shouldDisableAllInputs = userRole !== UserRole.ADMIN;

    const utils = trpc.useUtils();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const emailFileInputRef = useRef<HTMLInputElement>(null);

    const [formData, setFormData] = useState<PractitionerFormData>(initialFormData);
    const [initialData, setInitialData] = useState<PractitionerFormData | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // --- Photo Upload ---
    const uploadPhotoMutation = trpc.practitioners.uploadPhoto.useMutation({
        onSuccess: () => {
            utils.practitioners.getOne.invalidate({ id: practitionerId });
            toast.success("Foto hochgeladen.");
        },
        onError: (error) => {
            toast.error("Fehler beim Hochladen", { description: error.message });
        },
    });

    const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            const base64 = reader.result as string;
            uploadPhotoMutation.mutate({ practitionerId, imageBase64: base64 });
        };
        reader.readAsDataURL(file);
        e.target.value = "";
    };

    const isUploadingPhoto = uploadPhotoMutation.isPending;

    // --- Email Photo Upload ---
    const uploadEmailPhotoMutation = trpc.practitioners.uploadPhotoForEmails.useMutation({
        onSuccess: () => {
            utils.practitioners.getOne.invalidate({ id: practitionerId });
            toast.success("E-Mail-Foto hochgeladen.");
        },
        onError: (error) => {
            toast.error("Fehler beim Hochladen", { description: error.message });
        },
    });

    const handleEmailPhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            const base64 = reader.result as string;
            uploadEmailPhotoMutation.mutate({ practitionerId, imageBase64: base64 });
        };
        reader.readAsDataURL(file);
        e.target.value = "";
    };

    const isUploadingEmailPhoto = uploadEmailPhotoMutation.isPending;

    // --- Fetch Data ---
    const { data: practitionerData, isLoading: isLoadingPractitioner, error: practitionerError, refetch: refetchPractitioner } = trpc.practitioners.getOne.useQuery(
        { id: practitionerId },
        {
            refetchOnWindowFocus: false,
            // Ensure initial data includes the variant IDs
            // The getOne query was updated to return performableTreatmentVariantIds
        }
    );

    const { data: storesData, isLoading: isLoadingStores } = trpc.stores.getAllSimple.useQuery(undefined, {
        refetchOnWindowFocus: false,
    });

    const { data: categoriesData, isLoading: isLoadingCategories } = trpc.practitionerCategories.getAllSimple.useQuery(undefined, {
        refetchOnWindowFocus: false,
    });

    // Fetch the treatment hierarchy for the selector component
    const { data: treatmentHierarchyData, isLoading: isLoadingHierarchy } = trpc.treatments.getHierarchy.useQuery(undefined, {
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000, // Cache hierarchy for 5 minutes
    });

    const ignoreAvailabilities = practitionerData?.practitioner_category?.ignoreAvailabilities ?? false;


    // --- Mutation ---
    const updateMutation = trpc.practitioners.update.useMutation({
        onSuccess: async (data) => {
            toast.success("Behandlerdaten erfolgreich gespeichert!");
            // Refetch practitioner data to get updated state including variant IDs
            const refreshedData = await refetchPractitioner();
            if (refreshedData.data) {
                // Update initialData to the newly saved state for correct dirty check
                setInitialData({
                    title: refreshedData.data.title ?? null,
                    firstName: refreshedData.data.firstName ?? "",
                    lastName: refreshedData.data.lastName ?? "",
                    categoryId: refreshedData.data.categoryId,
                    bookingPriority: refreshedData.data.bookingPriority,
                    type: refreshedData.data.type ?? PractitionerType.DOCTOR,
                    storeIds: refreshedData.data.storeIds ?? [],
                    performableTreatmentVariantIds: refreshedData.data.performableTreatmentVariants?.map((assoc) => assoc.treatmentVariantId) || [],
                });
                // Reset form data *after* initialData is set if needed, or rely on effect below
                // setFormData(initialData); // Might cause flicker, useEffect approach is better
            } else {
                // Fallback: refetch utils cache if direct refetch fails
                await utils.practitioners.getOne.invalidate({ id: practitionerId });
            }

            setErrors({});
            setIsSubmitting(false);
        },
        onError: (error) => {
            toast.error(`Fehler beim Speichern: ${error.message}`);
            console.error("Mutation Error:", error);
            setIsSubmitting(false);
        },
    });

    // --- Populate Form on Initial Load ---
    useEffect(() => {
        if (practitionerData) {
            const loadedData = {
                title: practitionerData.title ?? null, // Use null consistently
                firstName: practitionerData.firstName ?? "",
                lastName: practitionerData.lastName ?? "",
                categoryId: practitionerData.categoryId,
                bookingPriority: practitionerData.bookingPriority,
                type: practitionerData.type ?? PractitionerType.DOCTOR,
                storeIds: practitionerData.storeIds ?? [],
                performableTreatmentVariantIds: practitionerData.performableTreatmentVariants?.map((assoc) => assoc.treatmentVariantId) ?? [],
            };
            console.log("Setting form data:", loadedData);
            setFormData(loadedData);
            setInitialData(loadedData); // Store for dirty check
        }
        // else if (!isLoadingPractitioner && !practitionerError) {
        //     // Handle case where data is null/undefined after loading without error
        //     console.warn(`Practitioner data for ID ${practitionerId} not found after load.`);
        //     // Potentially set form to initial empty state or show a specific message
        //      setFormData(initialFormData);
        //      setInitialData(initialFormData); // Ensure dirty check works correctly
        // }
    }, [practitionerData]); // Removed dependencies to avoid loops, only run when practitionerData changes


    // --- Memoized Options ---
    const storeOptions = useMemo<MultiSelectOption[]>(() =>
        storesData?.map(store => ({ value: store.id, label: store.name })) ?? [],
        [storesData]
    );

    const categoryOptions = useMemo(() =>
        categoriesData?.map(cat => ({ value: cat.id, label: cat.name })) ?? [],
        [categoriesData]
    );


    // --- Validation Logic ---
    const validateForm = useCallback((): boolean => {
        const result = formSchema.safeParse(formData);
        if (!result.success) {
            const newErrors: Record<string, string> = {};
            result.error.errors.forEach((err) => {
                // Check if path is an array and has elements
                if (Array.isArray(err.path) && err.path.length > 0) {
                    // Join path elements if nested, otherwise use the first element
                    const key = err.path.length > 1 ? err.path.join('.') : err.path[0];
                    if (typeof key === 'string' || typeof key === 'number') { // Ensure key is usable as index
                        newErrors[key] = err.message;
                    }
                }
            });
            setErrors(newErrors);
            return false;
        }
        setErrors({}); // Clear errors if validation succeeds
        return true;
    }, [formData]); // Re-create only when formData changes

    // --- Input Change Handlers ---
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setFormData(prev => ({
            ...prev,
            title: value === '' ? null : value, // Store null if empty
        }));
        if (errors.title) {
            setErrors(prev => ({ ...prev, title: '' }));
        }
    };

    const handleCategoryChange = (value: string) => {
        if (value.trim() === "") {
            return;
        }
        const newValue = value === NULL_CATEGORY_VALUE ? null : parseInt(value, 10); // Ensure base 10
        setFormData(prev => ({
            ...prev,
            categoryId: newValue,
        }));
        if (errors.categoryId) {
            setErrors(prev => ({ ...prev, categoryId: '' }));
        }
    };

    const handleBookingPriority = (value: string) => {
        if (value.trim() === "") {
            return;
        }
        const newValue = parseInt(value, 10); // Ensure base 10
        setFormData(prev => ({
            ...prev,
            bookingPriority: newValue,
        }));
        if (errors.bookingPriority) {
            setErrors(prev => ({ ...prev, bookingPriority: '' }));
        }
    };

    const handleStoreChange = (selectedValues: (string | number)[]) => {
        const numericIds = selectedValues.map(value => typeof value === 'string' ? parseInt(value, 10) : value); // Ensure base 10
        setFormData(prev => ({
            ...prev,
            storeIds: numericIds,
        }));
        if (errors.storeIds) {
            setErrors(prev => ({ ...prev, storeIds: '' }));
        }
    };

    // Handler for the Leistungen selector
    const handleLeistungenChange = useCallback((selectedVariantIds: string[]) => {
        setFormData(prev => ({
            ...prev,
            performableTreatmentVariantIds: selectedVariantIds,
        }));
        // Clear potential validation error for this field if needed (currently none)
        // if (errors.performableTreatmentVariantIds) {
        //     setErrors(prev => ({ ...prev, performableTreatmentVariantIds: '' }));
        // }
    }, []); // No dependencies needed as it only uses setFormData


    // --- Dirty Check ---
    const isDirty = useMemo(() => {
        if (!initialData) return false;
        // Deep comparison needed for arrays (storeIds, performableTreatmentVariantIds)
        return !isEqual(formData, initialData);
    }, [formData, initialData]);

    // --- Form Submission ---
    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        if (userRole !== UserRole.ADMIN) {
            toast.error("You don't have permission to do this.");
            return;
        }
        event.preventDefault();
        if (validateForm()) {
            setIsSubmitting(true);
            // Data should match the mutation input schema
            const dataToSend = {
                id: practitionerId,
                title: formData.title,
                firstName: formData.firstName,
                lastName: formData.lastName,
                categoryId: formData.categoryId,
                bookingPriority: formData.bookingPriority,
                type: formData.type,
                storeIds: formData.storeIds,
                performableTreatmentVariantIds: formData.performableTreatmentVariantIds,
            };
            updateMutation.mutate(dataToSend);
        } else {
            toast.error("Bitte überprüfen Sie die Eingaben.");
        }
    };

    // --- Loading State ---
    // Include hierarchy loading in the overall loading state for the form skeleton
    const isLoading = (isLoadingPractitioner && !initialData) || isLoadingStores || isLoadingCategories || isLoadingHierarchy;

    // --- Render Logic ---
    if (isLoading) { // Show skeleton if any core data is loading initially
        return (
            <Card>
                <CardHeader><CardTitle>Stammdaten Laden...</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    {/* Skeleton layout */}
                    <div className="space-y-2"><Skeleton className="h-6 w-1/4" /><Skeleton className="h-10 w-full" /></div>
                    <div className="space-y-2"><Skeleton className="h-6 w-1/4" /><Skeleton className="h-10 w-full" /></div>
                    <div className="space-y-2"><Skeleton className="h-6 w-1/4" /><Skeleton className="h-10 w-full" /></div>
                    <div className="space-y-2"><Skeleton className="h-6 w-1/4" /><Skeleton className="h-10 w-full" /></div>
                    <div className="space-y-2 md:col-span-2"><Skeleton className="h-6 w-1/4" /><Skeleton className="h-20 w-full" /></div>
                    {/* Skeleton for Leistungen */}
                    <div className="space-y-2 md:col-span-2"><Skeleton className="h-6 w-1/4" /><Skeleton className="h-32 w-full" /></div>
                </CardContent>
                <CardFooter><Skeleton className="h-10 w-24" /></CardFooter>
            </Card>
        );
    }

    // Error handling remains the same
    if (practitionerError) {
        return <Card><CardHeader><CardTitle>Fehler</CardTitle></CardHeader><CardContent>Fehler beim Laden der Behandlerdaten: {practitionerError.message}</CardContent></Card>;
    }
    if (!isLoadingPractitioner && !practitionerData && !practitionerError) {
        return <Card><CardHeader><CardTitle>Fehler</CardTitle></CardHeader><CardContent>Behandlerdaten konnten nicht abgerufen werden (ID: {practitionerId}).</CardContent></Card>;
    }


    const isDisabled = false; // Keep simple for now

    // Ensure formData is populated before rendering the dependent components
    // This check handles the brief moment between isLoading=false and useEffect populating the form
    // if (!initialData) {
    //      return <div>Initialisiere Formular...</div>; // Or a smaller skeleton/spinner
    // }

    console.log("Form Data for Render:", formData);
    console.log("Initial Data:", initialData);
    console.log("Is Dirty:", isDirty);
    console.log("Errors:", errors);


    return (
        <form onSubmit={handleSubmit}>
            <Card>
                <CardHeader><CardTitle>Stammdaten</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    {/* Photo */}
                    <div className="space-y-2 md:col-span-2">
                        <Label>Foto</Label>
                        <div className="flex items-start gap-6">
                            <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-full border bg-muted">
                                {practitionerData?.photoUrl ? (
                                    <img
                                        src={practitionerData.photoUrl}
                                        alt={`${practitionerData.firstName} ${practitionerData.lastName}`}
                                        className="h-full w-full object-cover"
                                    />
                                ) : (
                                    <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                                        <ImagePlus className="h-8 w-8" />
                                    </div>
                                )}
                            </div>
                            <div className="space-y-2">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handlePhotoSelect}
                                    className="hidden"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    disabled={isUploadingPhoto}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    {isUploadingPhoto ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                        <ImagePlus className="mr-2 h-4 w-4" />
                                    )}
                                    {practitionerData?.photoUrl ? "Foto ändern" : "Foto hochladen"}
                                </Button>
                                <p className="text-xs text-muted-foreground">
                                    JPG, PNG oder WebP. Wird auf max. 800px skaliert.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Photo for Emails */}
                    <div className="space-y-2 md:col-span-2">
                        <Label>Foto für E-Mails</Label>
                        <div className="flex items-start gap-6">
                            <div className="relative h-24 w-40 shrink-0 overflow-hidden rounded-lg border bg-muted">
                                {practitionerData?.photoForEmailsUrl ? (
                                    <img
                                        src={practitionerData.photoForEmailsUrl}
                                        alt={`${practitionerData.firstName} ${practitionerData.lastName} (E-Mail)`}
                                        className="h-full w-full object-cover"
                                    />
                                ) : (
                                    <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                                        <ImagePlus className="h-8 w-8" />
                                    </div>
                                )}
                            </div>
                            <div className="space-y-2">
                                <input
                                    ref={emailFileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleEmailPhotoSelect}
                                    className="hidden"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    disabled={isUploadingEmailPhoto}
                                    onClick={() => emailFileInputRef.current?.click()}
                                >
                                    {isUploadingEmailPhoto ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                        <ImagePlus className="mr-2 h-4 w-4" />
                                    )}
                                    {practitionerData?.photoForEmailsUrl ? "Foto ändern" : "Foto hochladen"}
                                </Button>
                                <p className="text-xs text-muted-foreground">
                                    Wird in E-Mails an Kunden angezeigt. JPG, PNG oder WebP.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Title */}
                    <div className="space-y-2">
                        <Label htmlFor="title">Titel</Label>
                        <Input
                            id="title"
                            name="title"
                            value={formData.title ?? ''}
                            onChange={handleTitleChange}
                            disabled={isDisabled || isSubmitting}
                        />
                        {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
                    </div>
                    {/* First Name */}
                    <div className="space-y-2">
                        <Label htmlFor="firstName">Vorname *</Label>
                        <Input
                            id="firstName"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            required
                            disabled={isDisabled || isSubmitting}
                        />
                        {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
                    </div>
                    {/* Last Name */}
                    <div className="space-y-2">
                        <Label htmlFor="lastName">Nachname *</Label>
                        <Input
                            id="lastName"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            required
                            disabled={isDisabled || isSubmitting}
                        />
                        {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
                    </div>

                    {/* Category Select */}
                    <div className="space-y-2">
                        <Label htmlFor="categoryId">Behandlerkategorie</Label>
                        {/* Check loadingCategories specifically for this select */}
                        {isLoadingCategories ? (
                            <Skeleton className="h-10 w-full" />
                        ) : (
                            <Select
                                // Ensure value is string or undefined for Select component
                                value={formData.categoryId !== null ? String(formData.categoryId) : undefined}
                                onValueChange={handleCategoryChange}
                                disabled={isDisabled || isSubmitting}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Kategorie auswählen..." />
                                </SelectTrigger>
                                <SelectContent id="categoryId">
                                    {/* Option for No Category */}
                                    {/* <SelectItem value={NULL_CATEGORY_VALUE}>Keine Kategorie</SelectItem> */}
                                    {/* Render only if categories exist */}
                                    {categoryOptions.length > 0 ? (
                                        categoryOptions.map((option) => (
                                            <SelectItem key={`cat_${option.value}`} value={String(option.value)}>
                                                {option.label}
                                            </SelectItem>
                                        ))
                                    ) : (
                                        <SelectItem value="no-options" disabled>Keine Kategorien verfügbar</SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                        )}
                        {errors.categoryId && <p className="mt-1 text-sm text-red-600">{errors.categoryId}</p>}
                    </div>

                    {/* Booking Priority */}
                    {!ignoreAvailabilities && (
                    <div className="space-y-2">
                        <Label htmlFor="bookingPriority">Terminpräferenz</Label>
                        {/* Check loadingCategories specifically for this select */}
                        <Select
                            // Ensure value is string or undefined for Select component
                            value={formData.bookingPriority !== null ? String(formData.bookingPriority) : undefined}
                            onValueChange={handleBookingPriority}
                            disabled={isDisabled || isSubmitting || shouldDisableAllInputs}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Präferenz auswählen..." />
                            </SelectTrigger>
                            <SelectContent id="bookingPriority">
                                <SelectItem value="100">Keine Präferenz</SelectItem>
                                <SelectItem value="50">Spezialbehandlungen bevorzugt</SelectItem>
                            </SelectContent>
                        </Select>
                        {errors.bookingPriority && <p className="mt-1 text-sm text-red-600">{errors.bookingPriority}</p>}
                    </div>
                    )}

                    {/* Stores (Filialen) */}
                    <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="storeIds">Filialen *</Label>
                        {isLoadingStores ? (
                            <Skeleton className="h-auto min-h-10 w-full" />
                        ) : (
                            <MultiSelect
                                options={storeOptions}
                                // Ensure selected expects string[] if MultiSelect requires it, otherwise keep numbers
                                // selected={formData.storeIds?.map(String) ?? []} // Example if it needs strings
                                selected={formData.storeIds ?? []} // Keep as numbers if MultiSelect handles it
                                onChange={handleStoreChange}
                                disabled={isDisabled || isSubmitting}
                                className="w-full"
                                placeholder="Filialen auswählen..."
                                searchPlaceholder="Filiale suchen..."
                                emptyText="Keine Filialen gefunden."
                                clearText="Auswahl aufheben"
                                selectAllText="Alle auswählen"
                                ariaLabel="Filialen auswählen"
                            />
                        )}
                        {errors.storeIds && <p className="mt-1 text-sm text-red-600">{errors.storeIds}</p>}
                    </div>

                    {/* Leistungen (Treatments) Section */}
                    {!ignoreAvailabilities && (
                    <div className="space-y-2 md:col-span-2">
                        <Label>Leistungen</Label>
                        {isLoadingHierarchy || !treatmentHierarchyData ? (
                            <Skeleton className="h-32 w-full" />
                        ) : (
                            <PractitionerLeistungenSelector
                                hierarchy={treatmentHierarchyData}
                                // Pass only the IDs currently selected in the form state
                                selectedVariantIds={formData.performableTreatmentVariantIds ?? []}
                                onChange={handleLeistungenChange} // Pass the handler to update form state
                                disabled={isDisabled || isSubmitting}
                            />
                        )}
                        {/* Add error display for this field if needed */}
                        {/* {errors.performableTreatmentVariantIds && <p className="mt-1 text-sm text-red-600">{errors.performableTreatmentVariantIds}</p>} */}
                    </div>
                    )}

                </CardContent>
                <CardFooter>
                    <Button type="submit" disabled={isDisabled || !isDirty || isSubmitting || shouldDisableAllInputs}>
                        {isSubmitting ? "Speichern..." : "Änderungen speichern"}
                    </Button>
                    {/* Debug Button - Remove later */}
                    {/* <Button type="button" variant="outline" className="ml-4" onClick={() => console.log({formData, initialData, isDirty})}>Debug State</Button> */}
                </CardFooter>
            </Card>
        </form>
    );
}